<script lang="ts">
  import { slide } from "svelte/transition"
  import { ChevronDown, ChevronRight } from "lucide-svelte"

  import Questions from "../Questions/Index.svelte"

  let props = $props()
  let { questions, response } = props

  let expand_details = $state(false)
  let pdf_mode = $state(props.pdf_mode ?? false)

  let alignment_questions = Object.entries(questions).filter(
    ([step, q]) => q.category === "alignment"
  )

  function answer_letter(i: number): string {
    if (i == 1) {
      return "A"
    }
    if (i == 2) {
      return "B"
    }
    if (i == 3) {
      return "C"
    }
    if (i == 4) {
      return "D"
    }
  }
</script>

<div class="rounded-lg border-gray-200 mt-8" class:border={!pdf_mode}>
  <h3
    class="text-lg lg:text-2xl font-semibold sm:font-bold text-black w-full py-3 px-5 bg-blue-900 text-white"
  >
    {#if response.score.alignment < 25}
      1. ALIGNMENT: There Is No Shared Direction
    {:else if response.score.alignment >= 25 && response.score.alignment < 50}
      1. ALIGNMENT: The Team Isn’t Clear on What Matters Most
    {:else if response.score.alignment >= 50 && response.score.alignment < 80}
      1. ALIGNMENT: Misalignment Is Costing You More Than You Think
    {:else if response.score.alignment >= 80 && response.score.alignment < 100}
      1. ALIGNMENT
    {:else if response.score.alignment === 100}
      1. ALIGNMENT
    {/if}
  </h3>
  <div class="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-6 p-4 lg:p-6">
    {#if response.score.alignment < 25}
      <div>
        <p class="text-base lg:text-lg/8 text-gray-600">
          People are guessing. Priorities shift weekly - or daily. You say
          something once, but it doesn’t land. Everyone’s busy… but not on the
          same mission.
        </p>

        <p class="mt-2 lg:mt-6 italic text-base lg:text-lg/8">
          This isn’t miscommunication. It’s organizational drift. And it’s
          compounding every day.
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 sm:py-4 sm:px-4">
        <p class="mt-2 text-base lg:text-lg/8 font-bold text-gray-700">
          Symptoms of deep misalignment:
        </p>
        <ul class="list-disc ml-6 sm:ml-8">
          <li class="mt-2 text-sm lg:text-base/7 text-gray-600">
            Every department operates in its own world
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Strategy is reactive, not consistent
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            You’re re-explaining the basics over and over
          </li>
        </ul>
      </div>
    {:else if response.score.alignment >= 25 && response.score.alignment < 50}
      <div>
        <p class="text-base lg:text-lg/8 text-gray-600">
          Without shared clarity, people fall into survival mode.
        </p>
        <p class="mt-2 lg:mt-6 text-base lg:text-lg/8 text-gray-600">
          That means inconsistent decisions, dropped priorities, and confusion
          that only gets noticed when results slip - or people leave.
        </p>

        <p class="mt-2 lg:mt-6 italic text-base lg:text-lg/8">
          Misalignment isn’t loud. It’s quiet - until it costs you a customer, a
          key employee, or critical momentum.
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 lg:py-4 lgsm:px-4">
        <p class="mt-2 text-base lg:text-lg/8 font-bold text-gray-700">
          Here’s how misalignment shows up:
        </p>
        <ul class="list-disc ml-6 lg:ml-8">
          <li class="mt-2 text-sm lg:text-base/7 text-gray-600">
            Everyone has a different definition of success
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Priorities are reactive, not strategic
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            You're repeating yourself in meetings - and still not getting
            follow-through
          </li>
        </ul>
      </div>
    {:else if response.score.alignment >= 50 && response.score.alignment < 80}
      <div>
        <p class="text-base lg:text-lg/8 text-gray-600">
          When we ask, “Could your team run without you for 30 days?” - we’re
          not talking about survival. We’re talking about leadership. Vision.
          Continuity. The ability to move fast without spinning out.
        </p>

        <p class="mt-2 lg:mt-6 italic text-base lg:text-lg/8">
          Misalignment is quiet… until it costs you a customer, a key hire, or
          momentum at the worst possible time.
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 lg:py-4 lg:px-4">
        <p class="mt-2 text-base lg:text-lg/8 font-bold text-gray-700">
          Here’s what misalignment looks like in practice:
        </p>
        <ul class="list-disc ml-6 lg:ml-8">
          <li class="mt-2 text-sm lg:text-base/7 text-gray-600">
            Your leaders “nod along,” but decisions stall
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Teams spend more time checking in than moving forward
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Priorities change, but not everyone gets the memo
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Energy drains as your people go through the motions, unclear on what
            matters
          </li>
        </ul>
      </div>
    {:else if response.score.alignment >= 80 && response.score.alignment < 100}
      <div>
        <p class="text-base lg:text-lg/8 text-gray-600">
          Even high-performing teams can fake alignment - until they hit
          resistance. You may be getting consistent output, but here’s the
          question:
        </p>
        <p
          class="py-2 px-2 lg:py-4 lg:px-4 text-base lg:text-lg/8 text-gray-600"
        >
          Are you all pulling in the same direction, at the same speed, with the
          same clarity?
        </p>

        <p class="mt-2 lg:mt-6 italic text-base lg:text-lg/8">
          Misalignment doesn’t scream. It simmers - and then it breaks things.
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 lg:py-4 lg:px-4">
        <p class="mt-2 text-base lg:text-lg/8 font-bold text-gray-700">
          Signs to watch:
        </p>
        <ul class="list-disc ml-6 lg:ml-8">
          <li class="mt-2 text-sm lg:text-base/7 text-gray-600">
            People agree in meetings but hesitate in action
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Priorities shift silently, without team-wide clarity
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            You still feel like you are the glue holding it all together
          </li>
        </ul>
      </div>
    {:else if response.score.alignment == 100}
      <div>
        <p class="text-base lg:text-lg/8 font-bold text-gray-700">
          You Believe Your Team Is Aligned… But Can They Deliver Under Pressure?
        </p>
        <p class="mt-6 text-base lg:text-lg/8 text-gray-600">
          Alignment can’t just live in your head.
        </p>
        <p class="mt-6 text-base lg:text-lg/8 text-gray-600">
          It needs to show up when you’re not there to explain it.
        </p>

        <p class="mt-4 lg:mt-8 italic text-base lg:text-lg/8">
          Even the best-run teams get blindsided when alignment goes untested.
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 lg:py-4 lg:px-4">
        <p class="mt-2 text-base lg:text-lg/8 font-bold text-gray-700">
          Here’s what unverified alignment looks like in elite teams:
        </p>
        <ul class="list-disc ml-6 lg:ml-8">
          <li class="mt-2 text-sm lg:text-base/7 text-gray-600">
            People nod in meetings, but interpretation drifts in the day-to-day
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Pressure exposes gaps that calm waters never reveal
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Your conviction masks others’ confusion
          </li>
        </ul>
      </div>
    {/if}
  </div>

  <div>
    {#if pdf_mode}
      <p class="text-xl font-bold text-gray-900 mt-2 lg:mt-6">
        Detailed question analysis
      </p>

      <div class="py-4 grid grid-cols-1 gap-y-2">
        {#each alignment_questions as [step, question]}
          {@const response_answer = response.response_answers.find(
            (ra) => ra.step == step
          )}
          <div class="py-2" style="break-after: page;">
            <p class="text-xl text-center text-blue-900 font-bold">
              Question {step}
            </p>
            <p class="text-lg text-gray-700 font-bold mt-4">
              {question.question}
            </p>

            <div class="mx-auto max-w-xl mt-6">
              {#each Object.entries(question.answers) as [i, answer]}
                <p
                  class="text-sm text-gray-600 py-1 px-2 flex"
                  class:font-bold={response_answer.answer == i}
                  class:bg-blue-100={response_answer.answer == i}
                >
                  {answer_letter(i)})
                  {answer}
                </p>
              {/each}
            </div>
            <p class="text-sm text-gray-700 font-semibold italic mt-6">
              Analysis of your answer:
            </p>
            <div class="col-span-2 mt-2">
              <Questions question={step} answer={response_answer.answer} />
            </div>
          </div>
        {/each}
      </div>
    {:else}
      <button
        class="w-full text-base lg:text-lg px-4 py-3 border-t border-gray-200 bg-gray-50 hover:bg-gray-100 hover:text-blue-700 text-left font-bold flex items-center justify-start"
        class:bg-gray-100={expand_details}
        class:text-blue-700={expand_details}
        onclick={() => (expand_details = !expand_details)}
      >
        {#if expand_details}
          <ChevronDown class="size-5 lg:size-6" />
        {:else}
          <ChevronRight class="size-5 lg:size-6" />
        {/if}
        View Detailed Question Analysis</button
      >

      {#if expand_details}
        <div
          transition:slide={{ duration: 600 }}
          class="px-2 sm:px-4 py-2 sm:py-4 grid grid-cols-1 gap-y-4 sm:gap-y-6"
        >
          {#each alignment_questions as [step, question]}
            {@const response_answer = response.response_answers.find(
              (ra) => ra.step == step
            )}
            <div
              class="grid grid-cols-1 lg:grid-cols-2 gap-4 bg-gray-50 border border-gray-100 rounded p-2 sm:p-4"
            >
              <div>
                <p
                  class="text-base sm:text-lg/8 lg:text-xl/8 text-gray-700 font-bold"
                >
                  {step}. {question.question}
                </p>
              </div>
              <div>
                {#each Object.entries(question.answers) as [i, answer]}
                  <p
                    class="text-sm sm:text-base/8 text-gray-600 py-1 px-2"
                    class:font-bold={response_answer.answer == i}
                    class:bg-blue-100={response_answer.answer == i}
                  >
                    {answer_letter(i)})
                    {answer}
                  </p>
                {/each}
              </div>

              <div class="col-span-1 lg:col-span-2">
                <Questions question={step} answer={response_answer.answer} />
              </div>
            </div>
          {/each}
        </div>
      {/if}
    {/if}
  </div>
</div>
